import type { Area } from "./permissions";

// The websites a hub manages (wrangler.jsonc SITES, per environment). kind: which dashboard screens the hub shows for
// it (src/sites/<kind>/); every kind talks to the website's admin API (Site Admin API v1) at apiBase.

export const KINDS = ["tasma", "reservella", "afaq"] as const;
export type SiteKind = (typeof KINDS)[number];
/** off: screens not switched on for this website yet, by their address under /sites/<id>/ (e.g. "blog", "stats"),
 *  for a live website that doesn't have what they need yet. Hidden only: the website refuses them by itself anyway. */
/** google: the website's Search Console property ("sc-domain:example.com" or its https:// address) and GA4 property id
 *  (digits), read live by the hub (worker/googleData.ts) for its Google screen. */
export type SiteConfig = { id: string; name: string; url: string; apiBase: string; kind: SiteKind; off?: string[]; google?: { searchConsole?: string; ga4?: string } };

const GOOGLE_OK = (g: unknown) =>
  g === undefined ||
  (Boolean(g) &&
    typeof g === "object" &&
    !Array.isArray(g) &&
    ((g as { searchConsole?: unknown }).searchConsole === undefined || (typeof (g as { searchConsole: unknown }).searchConsole === "string" && /^(sc-domain:[a-z0-9.-]+|https:\/\/\S+)$/.test((g as { searchConsole: string }).searchConsole))) &&
    ((g as { ga4?: unknown }).ga4 === undefined || (typeof (g as { ga4: unknown }).ga4 === "string" && /^\d{5,15}$/.test((g as { ga4: string }).ga4))));

/** The permission areas each kind of website uses, and what they cover there (the Users screen offers only these). */
export const KIND_AREAS: Record<SiteKind, Partial<Record<Area, string>>> = {
  tasma: {
    content: "Blog, projects, services, FAQ, page text, interface text",
    media: "Uploading, describing and deleting pictures and videos",
    leads: "Messages from the contact form",
    stats: "Visitors, pages, sources (view only)",
    scores: "The coming-soon game's leaderboard",
    history: "Earlier versions and putting one back",
    settings: "The coming-soon switch and publishing the site",
  },
  reservella: {
    content: "Hotels, destinations, blog guides (English and Arabic), search titles and descriptions",
    media: "Hotel photos; blog guide photos (upload: edit; choose an uploaded one: view)",
    leads: "Requests from the trip form",
    stats: "Visitors, pages, sources, clicks, trip requests sent (view only)",
    settings: "Contact details, social links, home page texts, publishing the site",
  },
  afaq: {
    content: "Projects, services, area pages, blog posts, page text and SEO, interface text",
    media: "Adding, ordering, describing and deleting project photos",
    history: "Every change, and undoing one",
    settings: "Company info (phones, address, areas served), redirects, robots.txt and llms.txt",
  },
};

/** https everywhere; http only on this computer (local tests). */
const WEB = /^(https:\/\/|http:\/\/localhost(:\d+)?(\/|$))/;

/** The SITES setting, checked: anything malformed is left out (and reported by /api/health). */
export function readSites(input: unknown): SiteConfig[] {
  // A JSON string too: `wrangler dev --var SITES:[…]` for local tests.
  if (typeof input === "string") {
    try {
      input = JSON.parse(input);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(input)) return [];
  return input.filter(
    (s): s is SiteConfig =>
      Boolean(s) &&
      typeof s.id === "string" &&
      /^[a-z0-9-]{1,40}$/.test(s.id) &&
      typeof s.name === "string" &&
      typeof s.url === "string" &&
      WEB.test(s.url) &&
      typeof s.apiBase === "string" &&
      WEB.test(s.apiBase) &&
      (KINDS as readonly unknown[]).includes(s.kind) &&
      (s.off === undefined || (Array.isArray(s.off) && s.off.every((p: unknown) => typeof p === "string" && /^[a-z0-9-]{1,40}$/.test(p)))) &&
      GOOGLE_OK(s.google),
  );
}
